<script setup lang="ts">
import {
  computed,
  onBeforeMount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IProfileSettingsConfirmationEmailSchema } from '@modules/profile-settings/submodules/confirmation-email/types';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  PROFILE_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import {
  useAdminProfileQuery,
  useAffiliateProfileQuery,
} from '@modules/profile-settings/queries';
import {
  ADMIN_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
} from '@modules/profile-settings/submodules/change-email/constants';
import { useProfileSettingsConfirmEmailMutation } from '@modules/profile-settings/submodules/confirmation-email/mutations';
import {
  ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
} from '@modules/profile-settings/submodules/general-settings/constants';
import { useUserSupervision } from '@modules/user/composables';
import { isString } from '@utils/guards';

const route = useRoute();
const router = useRouter();
const { isAdminNamespace } = useCurrentNamespace();
const { isSupervision } = useUserSupervision();
const { getTranslation } = useI18n();

const token = computed(() => (isString(route.params.token) ? route.params.token : ''));

const {
  data: profileAdmin,
  isLoading: isLoadingAdminData,
  waitLoaded: waitAdminDataLoaded,
  refetch: refetchAdmin,
} = useAdminProfileQuery();
const {
  data: profileAffiliate,
  isLoading: isLoadingAffiliate,
  waitLoaded: waitAffiliateDataLoaded,
  refetch: refetchAffiliate,
} = useAffiliateProfileQuery();
const {
  mutateAsync,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useProfileSettingsConfirmEmailMutation();

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const profile = computed(() => (
  isAdminNamespace.value ? profileAdmin.value : profileAffiliate.value
));

onBeforeMount(async () => {
  if (isAdminNamespace.value) {
    await waitAdminDataLoaded();
  } else {
    await waitAffiliateDataLoaded();
  }

  if (!profile.value?.unconfirmed_email) {
    void router.replace({
      name: isAdminNamespace.value ? ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME : AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
    });
  }
});

const isLoading = computed(() => (
  isAdminNamespace.value ? isLoadingAdminData.value : isLoadingAffiliate.value
));

const schema = computed<FormSchema<IProfileSettingsConfirmationEmailSchema>>(() => ({
  confirmation_token: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(translationGroup.value, 'CONFIRMATION_TOKEN').value,
    required: true,
    value: token.value,
  },
}));

async function onSubmit(values: IProfileSettingsConfirmationEmailSchema): Promise<void> {
  await mutateAsync(values);

  if (isAdminNamespace.value) {
    const response = await refetchAdmin();
    if (!response.data?.unconfirmed_email) {
      void router.push({
        name: ADMIN_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
      });
    }
  } else {
    const response = await refetchAffiliate();
    if (!response.data?.unconfirmed_email) {
      void router.push({
        name: AFFILIATE_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
      });
    }
  }
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'ConfirmationEmailPage'"
    :loading="isLoading"
    min-height="200px"
  >
    <div>
      <p class="mb-4">
        {{ $getTranslation(translationGroup, 'CHECK_MAILBOX_MESSAGE') }}
      </p>
      <VForm
        :schema="schema"
        :readonly="!$hasPermission(PROFILE_SETTINGS_PERMISSIONS.EDIT) || isSupervision"
        :submit-btn-label="$getTranslation(translationGroup, 'SAVE')"
        :custom-errors="errors"
        :is-loading="isLoadingSave"
        @interacted="clearError"
        @submit="onSubmit"
      />
    </div>
  </VLoader>
</template>
